import type { Coords } from 'google-map-react';
import { GOOGLE_MAP_STYLES } from './googleMapStyles';
import type { AddressCountry } from '@archipro-website/localisation';
import { getCountryCodeByName } from '~/modules/location/util';
import type { LocationDataType } from '@modules/professional/type/contact-page-types';

export interface ArchiProMapVariables {
    center: Coords;
    zoom: {
        search: number;
        click: number;
        max: number;
        min: number;
    };
    latitudeOffset: number;
    restriction?: {
        latLngBounds: {
            east: number;
            west: number;
            north: number;
            south: number;
        };
        strictBounds: boolean;
    };
}

interface CountryMapVariables {
    DESKTOP: ArchiProMapVariables;
    MOBILE: ArchiProMapVariables;
}

/** Derive the mobile map variables from desktop: same bounds, wider latitude offset, lower search zoom. */
const withMobileVariant = (
    desktop: ArchiProMapVariables,
    mobileZoom: Pick<ArchiProMapVariables['zoom'], 'search' | 'min'> &
        Partial<Pick<ArchiProMapVariables['zoom'], 'click' | 'max'>>,
    center?: Coords,
): CountryMapVariables => ({
    DESKTOP: desktop,
    MOBILE: {
        ...desktop,
        ...(center ? { center } : {}),
        latitudeOffset: 0.325,
        zoom: {
            click: desktop.zoom.click,
            max: desktop.zoom.max,
            ...mobileZoom,
        },
    },
});

const NZ_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: -41.2851, lng: 174.776 },
    zoom: {
        search: 5.5,
        click: 9,
        max: 19,
        min: 5.5,
    },
    latitudeOffset: 0.04,
    restriction: {
        latLngBounds: {
            east: 190.00304,
            west: 155.696896,
            north: -24.75318,
            south: -53.021069,
        },
        strictBounds: false,
    },
};

const AU_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: -27.909965, lng: 135.74815 },
    zoom: {
        search: 4,
        click: 9,
        max: 19,
        min: 4,
    },
    latitudeOffset: 0.045,
    restriction: {
        latLngBounds: {
            east: 168.569469029,
            west: 98.338953078,
            north: 10.6681857235,
            south: -53.6345972634,
        },
        strictBounds: false,
    },
};

const US_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: 39.8283, lng: -98.5795 },
    zoom: {
        search: 4,
        click: 9,
        max: 19,
        min: 3,
    },
    latitudeOffset: 0.045,
    restriction: {
        latLngBounds: {
            east: -66.93457,
            west: -125.0,
            north: 49.384358,
            south: 24.396308,
        },
        strictBounds: false,
    },
};

/** Mainland bounds plus the Arctic archipelago, so Yukon and Nunavut stay reachable. */
const CA_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: 56.1304, lng: -106.3468 },
    zoom: {
        search: 3.6,
        click: 9,
        max: 19,
        min: 3,
    },
    latitudeOffset: 0.045,
    restriction: {
        latLngBounds: {
            east: -52.6363,
            west: -141.0027,
            north: 83.1103,
            south: 41.6765,
        },
        strictBounds: false,
    },
};

/** Great Britain, Northern Ireland and the Shetlands. */
const GB_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: 54.0023, lng: -2.5478 },
    zoom: {
        search: 5.2,
        click: 9,
        max: 19,
        min: 4.5,
    },
    latitudeOffset: 0.04,
    restriction: {
        latLngBounds: {
            east: 1.7689,
            west: -8.6499,
            north: 60.8608,
            south: 49.8639,
        },
        strictBounds: false,
    },
};

/**
 * Used when pins span more than one country, or sit in a country with no configuration.
 * No `restriction`: a single-country bounding box would make out-of-box markers unreachable.
 */
const WORLD_MAP_VARIABLES_DESKTOP: ArchiProMapVariables = {
    center: { lat: 20, lng: 0 },
    zoom: {
        search: 2,
        click: 9,
        max: 19,
        min: 1,
    },
    latitudeOffset: 0.045,
};

const MAP_VARIABLES: Record<AddressCountry, CountryMapVariables> = {
    NZ: withMobileVariant(
        NZ_MAP_VARIABLES_DESKTOP,
        { search: 5, min: 5 },
        { lat: -40.2851, lng: 172.576 },
    ),
    AU: withMobileVariant(AU_MAP_VARIABLES_DESKTOP, { search: 3.4, min: 3.4 }),
    US: withMobileVariant(US_MAP_VARIABLES_DESKTOP, { search: 3.4, min: 3.4 }),
    CA: withMobileVariant(CA_MAP_VARIABLES_DESKTOP, { search: 3, min: 3 }),
    GB: withMobileVariant(GB_MAP_VARIABLES_DESKTOP, { search: 4.5, min: 4.5 }),
};

const WORLD_MAP_VARIABLES: CountryMapVariables = withMobileVariant(
    WORLD_MAP_VARIABLES_DESKTOP,
    { search: 2, min: 1 },
);

export const getArchiProMapVariables = (
    countryCode: string | null | undefined,
    isMobile = false,
): ArchiProMapVariables => {
    const type = isMobile ? 'MOBILE' : 'DESKTOP';
    const code = getCountryCodeByName(countryCode ?? undefined);

    // A country with no box of its own gets the unrestricted world view rather than another
    // country's bounds. Every ISO code reaches here -- a location can sit in any country the
    // place data covers, and a bounding box is editorial, not something the import decides.
    const variables =
        (code ? MAP_VARIABLES[code] : undefined) ?? WORLD_MAP_VARIABLES;

    return variables[type];
};

/** A pin at 0,0 is a failed geocode, not a real place in the Gulf of Guinea. */
export const hasMapCoordinates = (location: LocationDataType): boolean => {
    const coordinates = location.MapCoordinates;
    return (
        !!coordinates &&
        (coordinates.Latitude !== 0 || coordinates.Longitude !== 0)
    );
};

/**
 * Map settings for a set of pins.
 *
 * Centre and bounds come from the pins' countries; the professional's market is only a
 * fallback. `restriction` clamps the viewport even with `strictBounds: false`, so a market
 * box must not be applied when pins sit outside it. Multi-country pin sets use the
 * unrestricted world view.
 */
export const getMapVariablesForLocations = (
    locations: LocationDataType[],
    fallbackCountry: string | null | undefined,
    isMobile = false,
): ArchiProMapVariables => {
    const countries = new Set(
        locations
            .filter(hasMapCoordinates)
            .map((location) => getCountryCodeByName(location.Country))
            .filter((code) => code !== null),
    );

    if (countries.size > 1) {
        return WORLD_MAP_VARIABLES[isMobile ? 'MOBILE' : 'DESKTOP'];
    }

    const [only] = countries;

    return getArchiProMapVariables(only ?? fallbackCountry, isMobile);
};

export const INCLUDE_LINKED_PROFILES = true;
export const SCROLL_TO_MAP_AFTER_CLICK_LINK = false;
export const SCROLL_TO_MAP_AFTER_CLICK_MAP_BUTTON = false;

export const MOBILE_INITIAL_LOCATION_NUM = 8;
export const MOBILE_SEARCH_MAP_OFFSET = 140;
export const MOBILE_MAP_TOP_GAP = 120;
export const DESKTOP_MAP_TOP_GAP = 180;

export { GOOGLE_MAP_STYLES };
